import { getDepositSearch } from 'api/v1/batchTxnDetail'
import { getHistoryListApi, HistoryType } from 'api/v1/txHistory'
import {
  DefaultDepositConfirmations,
  DEPOSIT_PENDING_TRANSACTION_TIMEOUT,
  POLLING_DEPOSIT_CONFIRMATIONS_INTERVAL,
} from 'constants/deposit'
import { ethers } from 'ethers'
import { compact } from 'lodash'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { store } from 'store'
import { useAccountId } from 'store/account/hooks'
import { useDepositEthHash } from 'store/history/hooks'
import { L1ChainId } from 'types'
import { useInterval } from 'usehooks-ts'
import {
  removeMatchedDepositPendingTxAction,
  updateDepositStatus,
  updateEthHash,
  updateQueryL2Hash,
  updateTxConfirmations,
} from './actions'
import { usePendingTxs, useQueryL2Hash } from './hook'
import { DepositStatus, EthHashItem } from './types'
import { NetworkRegistry } from '../../utils/networkRegistry'
import { useUnexpiredDepositPendingTxs } from '../../hooks/web3/useUnexpiredDepositPendingTxs'
import dayjs from 'dayjs'
import { useBlockConfirmedDepositPendingTxs } from '../../hooks/web3/useBlockConfirmedDepositPendingTxs'

function getDepositConfirmations(chainId: L1ChainId) {
  const network = NetworkRegistry.byL1(chainId)
  if (!network) {
    console.error('Cannot find valid network data', chainId)
    return DefaultDepositConfirmations
  }

  return network?.depositConfirmation ?? DefaultDepositConfirmations
}

function useCheckZKEXDepositStatus() {
  const dispatch = useDispatch()
  const pendingTxs = useBlockConfirmedDepositPendingTxs()
  const accountId = useAccountId()

  const checkDepositStatus = async () => {
    const { results = [] } =
      (await getHistoryListApi({
        type: HistoryType.Deposit,
        account: accountId!,
        limit: 10,
        offsetId: 0,
      }).then((r) => r.data)) ?? {}

    if (!results.length) return

    dispatch(
      removeMatchedDepositPendingTxAction(
        compact(results.map((v) => v.ethHash)),
      ),
    )

    const batchStatus = results.map((item) => ({
      ethHash: item.ethHash!, // ethHash is existed in deposit history
      status: DepositStatus.ZkexConfirmed,
    }))
    dispatch(updateDepositStatus(batchStatus))
  }

  useInterval(
    checkDepositStatus,
    accountId !== undefined && pendingTxs?.length
      ? POLLING_DEPOSIT_CONFIRMATIONS_INTERVAL
      : null,
  )
}

async function getDepositBatchResultFromLayer1(ethHashItem: EthHashItem) {
  if (!ethHashItem?.hash) {
    console.error('Missing or invalid deposit hash', ethHashItem.hash)
    return
  }
  if (!ethHashItem?.chainId) {
    console.error('Missing or invalid deposit chainId', ethHashItem.chainId)
    return
  }

  const network = NetworkRegistry.byL1(ethHashItem.chainId)
  if (!network) {
    console.error('Cannot find valid network data', JSON.stringify(ethHashItem))
    return
  }

  const provider = new ethers.providers.JsonRpcProvider(network.rpcUrl)
  return provider?.getTransactionReceipt(ethHashItem.hash).catch((e) => {
    console.error(e)
  })
}
async function getDepositSearchByEthHash(ethHash: string) {
  return await getDepositSearch(ethHash)
}

async function checkBlockConfirmations(depositEthHash: EthHashItem[]) {
  if (!depositEthHash.length) return

  let queryL2Hash: string[] = []
  Promise.all(
    depositEthHash.map((x) => getDepositBatchResultFromLayer1(x)),
  ).then((res) => {
    res?.forEach((receipt, index) => {
      const { status, confirmations = 0, transactionHash = '' } = receipt ?? {}

      // blockchain executed status: 1 success, 0 failed
      if (status === 1) {
        if (
          confirmations >=
          getDepositConfirmations(depositEthHash[index].chainId)
        ) {
          store.dispatch(
            updateDepositStatus([
              {
                ethHash: transactionHash,
                status: DepositStatus.BlockConfirmed,
              },
            ]),
          )
          store.dispatch(
            updateTxConfirmations([
              {
                txHash: transactionHash,
                confirmations: getDepositConfirmations(
                  depositEthHash[index].chainId,
                ),
              },
            ]),
          )
          queryL2Hash.push(transactionHash)
        } else {
          store.dispatch(
            updateDepositStatus([
              {
                ethHash: transactionHash,
                status: DepositStatus.BlockConfirmations,
              },
            ]),
          )

          store.dispatch(
            updateTxConfirmations([{ txHash: transactionHash, confirmations }]),
          )
        }
      } else if (status === 0) {
        store.dispatch(
          updateDepositStatus([
            {
              ethHash: transactionHash,
              status: DepositStatus.BlockFailed,
            },
          ]),
        )
      } else {
        store.dispatch(
          updateDepositStatus([
            {
              ethHash: transactionHash,
              status: DepositStatus.BlockPending,
            },
          ]),
        )
      }
    })
    store.dispatch(updateQueryL2Hash(queryL2Hash))
  })
}

function checkZklinkStatus(queryL2Hash: string[]) {
  Promise.all(queryL2Hash.map((m) => getDepositSearchByEthHash(m))).then(
    (res) => {
      res?.forEach((n, i) => {
        if (n?.data?.result?.detail) {
          store.dispatch(
            updateDepositStatus([
              {
                ethHash: queryL2Hash[i],
                status: DepositStatus.ZkLinkConfirmed,
              },
            ]),
          )
        }
      })
    },
  )
}
function useWatchDepositList() {
  const dispatch = useDispatch()
  const pendingTxs = useUnexpiredDepositPendingTxs()
  const depositEthHash = useDepositEthHash()
  const queryL2Hash = useQueryL2Hash()

  useEffect(() => {
    if (!pendingTxs.length) return

    let ethHashList: EthHashItem[] = []
    pendingTxs.forEach((item) => {
      const depositStatus = store.getState()?.deposit?.depositStatus
      // if the deposit status is not exist, set it to loading
      if (!depositStatus[item.ethHash]) {
        dispatch(
          updateDepositStatus([
            {
              ethHash: item.ethHash,
              status: DepositStatus.Loading,
            },
          ]),
        )
      }
      // If the deposit status is zkex confirmed, this deposit is already handled, no need to check again
      if (depositStatus[item.ethHash] === DepositStatus.ZkexConfirmed) return
      ethHashList.push({
        hash: item.ethHash,
        chainId: item.layerOneChainId,
      })
    })
    dispatch(updateEthHash(ethHashList))
  }, [dispatch, pendingTxs])

  useEffect(() => {
    if (!depositEthHash.length) return

    checkBlockConfirmations(depositEthHash)
  }, [depositEthHash])

  useInterval(
    () => {
      checkBlockConfirmations(depositEthHash)
    },
    depositEthHash?.length ? POLLING_DEPOSIT_CONFIRMATIONS_INTERVAL : null,
  )

  useEffect(() => {
    if (!queryL2Hash.length) return
    checkZklinkStatus(queryL2Hash)
  }, [queryL2Hash])

  useInterval(
    () => {
      checkZklinkStatus(queryL2Hash)
    },
    queryL2Hash?.length ? POLLING_DEPOSIT_CONFIRMATIONS_INTERVAL : null,
  )
}

function useAutoRemoveExpiredDepositPendingTxs() {
  const pendingTxs = usePendingTxs()
  const dispatch = useDispatch()
  useInterval(() => {
    if (!pendingTxs?.length) return
    const now = Date.now()
    const expiredHashes = pendingTxs.map((tx) => {
      if (
        now - dayjs(tx.createdAt).valueOf() >
        DEPOSIT_PENDING_TRANSACTION_TIMEOUT
      ) {
        return tx.ethHash
      } else {
        return ''
      }
    })
    dispatch(removeMatchedDepositPendingTxAction(compact(expiredHashes)))
  }, 1000 * 10)
}
export default function Updater() {
  useCheckZKEXDepositStatus()
  useWatchDepositList()
  useAutoRemoveExpiredDepositPendingTxs()

  return null
}
